import React from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { notifyInfo, notifyError } from '../../utils/notifier';
import {
  activateUser,
  validateUniqueEmail,
  deactivateUser,
  isnConnectionTest,
} from '../../actions/profile/apiRequests';
import messages from '../../constants/messages';
import Page from '../shared/Page';
import Button from '../shared/Button';
import ActionModal from '../shared/ActivationModal';
import ProfileInfo from './ProfileInfo';
import UserLoginInfo from './UserLoginInfo';
import UomInfo from './UomInfo';
import ProfileSignature from './ProfileSignature';
import IsnIntegrationParams from './IsnIntegrationParams';
import { fetchCompany } from '../../actions/companies/apiRequests';
import Modal from '../shared/Modal';
import { getBreadCrumbs } from '../../utils/breadCrumbs';
import { MANAGE_USERS, PAGE_PATHS, DASHBOARD } from '../../constants/pathsRoots';
import { COMPANY_ADMIN } from '../../constants/userRoles';

export class ProfilePage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      profile: {
        ...props.profile,
        password: '',
        confirmPassword: '',
      },
      signature: null,
      signatureDeleted: false,
      errors: {},
      saving: false,
      userStatusModal: false,
      showValidationErrors: false,
      fileUploadError: false,
      hasActiveCompanyAdmin: props.hasActiveCompanyAdmin,
      isnConnectionTesting: false,
      isnConnectionTestResult: null,
    };
    this.onSave = this.onSave.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.onFieldValidate = this.onFieldValidate.bind(this);
    this.onLoadSignature = this.onLoadSignature.bind(this);
    this.onClearSignature = this.onClearSignature.bind(this);
    this.toggleUserStatusModal = this.toggleUserStatusModal.bind(this);
    this.onStatusChangeSuccess = this.onStatusChangeSuccess.bind(this);
    this.onStatusChangeError = this.onStatusChangeError.bind(this);
    this.onSaveError = this.onSaveError.bind(this);
    this.showModalError = this.showModalError.bind(this);
    this.hideModalError = this.hideModalError.bind(this);
    this.autocompleteFields = this.autocompleteFields.bind(this);
    this.onIsnConnectionTest = this.onIsnConnectionTest.bind(this);
    this.setIsnConnectionTestingState = this.setIsnConnectionTestingState.bind(this);
  }

  static get propTypes() {
    return {
      profile: PropTypes.object,
      title: PropTypes.string,
      onSave: PropTypes.func,
      onCancel: PropTypes.func,
      editProfileInfo: PropTypes.bool,
      editLoginInfo: PropTypes.bool,
      editUom: PropTypes.bool,
      editSignature: PropTypes.bool,
      editCompany: PropTypes.bool,
      editEmail: PropTypes.bool,
      editRole: PropTypes.bool,
      editStatus: PropTypes.bool,
      newUser: PropTypes.bool,
      companyUser: PropTypes.bool,
      passwordRequired: PropTypes.bool,
      location: PropTypes.object,
      hasActiveCompanyAdmin: PropTypes.bool,
    };
  }

  static get defaultProps() {
    return {
      profile: {},
      editProfileInfo: true,
      editLoginInfo: false,
      editUom: true,
      editSignature: true,
      passwordRequired: false,
    };
  }

  get isValid() {
    const { errors } = this.state;
    return !Object.values(errors).filter((value) => !!value).length;
  }

  get isPasswordConfirmed() {
    const { passwordRequired } = this.props;
    const { confirmPassword } = this.state.profile;
    return !passwordRequired || confirmPassword;
  }

  get actionButtons() {
    return [
      <If key="deactivate" condition={this.props.editStatus}>
        <Button kind="danger" className="mr-4" onClick={this.toggleUserStatusModal}>
          {this.state.profile.enabled ? 'Deactivate' : 'Activate'}
        </Button>
      </If>,
      <Button key="cancel" kind="primary" className="mr-3" onClick={this.props.onCancel}>
        Cancel
      </Button>,
      <Link
        to={{
          pathname: '/users',
        }}
        key="save"
      >
        <Button key="save" kind="primary" style={{ width: '150px' }} disabled={this.state.saving} onClick={this.onSave}>
          {this.state.saving ? <i className="fa fa-spin fa-spinner" /> : 'Save changes'}
        </Button>
      </Link>,
    ];
  }

  componentDidMount() {
    const { newUser } = this.props;
    const { companyId } = this.state.profile;
    newUser && this.autocompleteFields(companyId);
  }

  autocompleteFields(companyId) {
    const addressFields = ['address1', 'address2', 'postalCode', 'country', 'city', 'state', 'phone', 'fax', 'county'];
    if (companyId) {
      fetchCompany(companyId).then((companyInfo) => {
        addressFields.forEach((name) => {
          this.onFieldChange(name, companyInfo[name] ? companyInfo[name] : '');
        });

        const hasActiveCompanyAdmin = companyInfo.admins && companyInfo.admins.length > 0;
        this.setState({ hasActiveCompanyAdmin });
        if (!hasActiveCompanyAdmin) {
          this.onFieldChange('role', COMPANY_ADMIN);
        }
      });
    }
  }

  onSave(e) {
    e.preventDefault();
    const { profile, signature, signatureDeleted } = this.state;
    const { onSave, onCancel } = this.props;

    this.setState({ showValidationErrors: true });
    if (!this.isValid || !this.isPasswordConfirmed) {
      return;
    }

    this.setState({ saving: true });
    onSave(profile, signature, signatureDeleted)
      .then(() => {
        notifyInfo(messages.common.saveSuccess);
      })
      .catch(this.onSaveError)
      .then(() => this.setState({ saving: false }));
  }

  onFieldChange(name, value) {
    this.setState((prevState) => ({
      profile: {
        ...prevState.profile,
        [name]: value,
      },
    }));
  }

  onFieldValidate(name, error) {
    this.setState((prevState) => ({
      errors: {
        ...prevState.errors,
        [name]: error,
      },
    }));
  }

  onLoadSignature(file) {
    this.setState({
      signature: file,
    });
  }

  onClearSignature() {
    this.setState({
      signature: null,
      signatureDeleted: true,
    });
  }

  onStatusChangeSuccess() {
    this.onFieldChange('enabled', !this.state.profile.enabled);
    this.toggleUserStatusModal();
    notifyInfo(messages.common.saveSuccess);
  }

  onStatusChangeError(error) {
    let message = messages.common.error;
    if (error.status === 400) {
      message = messages.request.userActivationError;
    }
    this.toggleUserStatusModal();
    notifyError(message);
  }

  toggleUserStatusModal() {
    this.setState(({ userStatusModal }) => ({
      userStatusModal: !userStatusModal,
    }));
  }

  onSaveError({ status, response = {} }) {
    let message = messages.common.error;
    if (response.errors && response.errors.Email) {
      message = messages.validation.common;
      this.onFieldValidate('email', messages.validation.emailNotUnique);
    }
    notifyError(message);
  }

  onIsnConnectionTest() {
    const { profile, isnConnectionTesting } = this.state;

    const test = {
      accessKey: profile.accessKey,
      secretKey: profile.secretKey,
      onlyCheck: !(!profile.accessKey && !profile.secretKey),
    };

    this.setIsnConnectionTestingState(!isnConnectionTesting);
    this.setState({
      isnConnectionTestResult: {},
    });

    isnConnectionTest(profile.id, test)
      .then((result) => {
        this.setState({
          isnConnectionTestResult: result,
        });
        this.setIsnConnectionTestingState(isnConnectionTesting);
      })
      .catch((error) => {
        this.setState({
          isnConnectionTestResult: {
            message: error.message,
          },
        });
        this.setIsnConnectionTestingState(isnConnectionTesting);
      });
  }

  setIsnConnectionTestingState(isnConnectionTesting) {
    this.setState({
      isnConnectionTesting,
    });
  }

  activateUser(email, id) {
    return validateUniqueEmail(email).then(() => activateUser(id));
  }

  showModalError() {
    this.setState({
      fileUploadError: true,
    });
  }

  hideModalError() {
    this.setState({
      fileUploadError: false,
    });
  }

  getBreadCrumbs(title) {
    const { pathname } = this.props.location;
    return title !== 'My Profile'
      ? getBreadCrumbs(title, pathname, [PAGE_PATHS[DASHBOARD], PAGE_PATHS[MANAGE_USERS]])
      : getBreadCrumbs(title, pathname, []);
  }

  render() {
    const {
      profile,
      signature,
      signatureDeleted,
      userStatusModal,
      errors,
      showValidationErrors,
      fileUploadError,
      isnConnectionTesting,
      isnConnectionTestResult,
    } = this.state;
    const {
      title,
      editProfileInfo,
      editLoginInfo,
      editUom,
      editSignature,
      passwordRequired,
      editCompany,
      editEmail,
      editRole,
      newUser,
      companyUser,
    } = this.props;
    const { hasActiveCompanyAdmin } = this.state;

    return (
      <Page header={title} actions={this.actionButtons} breadCrumbs={this.getBreadCrumbs(title)}>
        <Container fluid className="px-0 mx-0">
          <Row className="text-right mb-4">
            <Col lg={6}>
              <ProfileInfo
                editMode={editProfileInfo}
                profile={profile}
                errors={errors}
                showErrors={showValidationErrors}
                onFieldChange={this.onFieldChange}
                onFieldValidate={this.onFieldValidate}
              />
            </Col>
            <Col lg={6}>
              <UserLoginInfo
                editMode={editLoginInfo}
                companyName={profile.companyName}
                companyId={profile.companyId}
                email={profile.email}
                userType={profile.role}
                password={profile.password}
                confirmPassword={profile.confirmPassword}
                passwordRequired={passwordRequired}
                editCompany={editCompany}
                editEmail={editEmail}
                editRole={editRole}
                newUser={newUser}
                hasActiveCompanyAdmin={hasActiveCompanyAdmin}
                companyUser={companyUser}
                errors={errors}
                showErrors={showValidationErrors}
                onFieldChange={this.onFieldChange}
                onFieldValidate={this.onFieldValidate}
                autocompleteFields={this.autocompleteFields}
              />
              <If condition={editUom}>
                <UomInfo uom={profile.uom} onSwitch={(e) => this.onFieldChange('uom', e.target.value)} />
              </If>
              <If condition={editSignature}>
                <ProfileSignature
                  signatureBase64={profile.signatureBase64}
                  signature={signature}
                  signatureDeleted={signatureDeleted}
                  onLoadSignature={this.onLoadSignature}
                  onClearSignature={this.onClearSignature}
                  showModalError={this.showModalError}
                />
              </If>
              <If condition={profile.isIsn}>
                <IsnIntegrationParams
                  isnKey={profile.isnKey}
                  isnDomain={profile.isnDomain}
                  accessKey={profile.accessKey}
                  secretKey={profile.secretKey}
                  isIsnCredentialsPresent={profile.isIsnCredentialsPresent}
                  isnConnectionTesting={isnConnectionTesting}
                  isnConnectionTestResult={isnConnectionTestResult}
                  onFieldChange={this.onFieldChange}
                  onIsnConnectionTest={this.onIsnConnectionTest}
                />
              </If>
            </Col>
          </Row>
        </Container>
        <Modal
          isOpen={fileUploadError}
          showCancelButton={false}
          onConfirm={this.hideModalError}
          header="File upload error"
        >
          The file could not be uploaded. Make sure the file has an image extension and its size less than 5Mb
        </Modal>
        <ActionModal
          message={`Are you sure you want to ${profile.enabled ? 'deactivate' : 'activate'} this user?`}
          isOpen={userStatusModal}
          action={() => (profile.enabled ? deactivateUser(profile.id) : this.activateUser(profile.email, profile.id))}
          onCancel={this.toggleUserStatusModal}
          onSuccess={this.onStatusChangeSuccess}
          onError={this.onStatusChangeError}
        />
      </Page>
    );
  }
}

export default ProfilePage;



// WEBPACK FOOTER //
// src/components/profile/ProfilePage.jsx